import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import Modal from "./Modal";
import { api, ApiError, type FeedbackKind } from "../lib/api";

/**
 * Right-click → "Send feedback" → snip a region → add a note → it files to the
 * Feedback panel (and the server's existing notice emails whoever is armed on
 * FEEDBACK_NOTIFY_TO).
 *
 * WHY THIS EXISTS (Jonathan, 2026-10-21). The Feedback page works but nobody
 * uses it: it is three menus deep, and you still have to take an OS screenshot
 * and upload the file. So the loop that is meant to catch the live site's UI
 * bugs sits idle. This removes every step: right-click anywhere, drag a box,
 * type a line, done. Same `POST /api/feedback` + `PUT …/screenshot` the panel
 * already uses -- only the capture is new.
 *
 * THE RIGHT-CLICK IS NOT GRABBED EVERYWHERE. In a field or over a text
 * selection the native menu still opens -- copy/paste is what a right-click is
 * for there, and a financial screen is full of numbers people copy. Everywhere
 * else the custom menu opens instead. It opens a MENU, not the snip tool, so a
 * stray right-click can't start a capture by itself (Jonathan's ask).
 *
 * The capture is html2canvas, loaded only when someone actually snips (a
 * dynamic import, so it never touches the main bundle). Every piece of this
 * tool's own chrome carries `data-snip-ui` and is passed to `ignoreElements`,
 * so the menu/overlay/indicator can never end up inside the shot.
 */

type Phase = "idle" | "menu" | "snip" | "capturing" | "review";
type Point = { x: number; y: number };
type Rect = { x: number; y: number; w: number; h: number };

const KIND_LABEL: Record<FeedbackKind, string> = {
  bug: "Bug",
  change: "Change",
  question: "Question",
  other: "Other",
};

function rectOf(a: Point, b: Point): Rect {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    w: Math.abs(a.x - b.x),
    h: Math.abs(a.y - b.y),
  };
}

export default function FeedbackSnip() {
  const location = useLocation();
  const [phase, setPhase] = useState<Phase>("idle");
  const [menuAt, setMenuAt] = useState<Point | null>(null);
  const [shot, setShot] = useState<Blob | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const reset = useCallback(() => {
    setPhase("idle");
    setMenuAt(null);
  }, []);

  // Right-click opens the one-button menu -- unless the native menu is the
  // right call (editing a field, or a live text selection to copy).
  useEffect(() => {
    function onContext(event: MouseEvent) {
      const target = event.target as HTMLElement | null;
      // A field always keeps the native menu -- copy/paste/spellcheck is what a
      // right-click is for there, including inside our own review modal.
      if (
        target?.closest(
          "input, textarea, select, [contenteditable=''], [contenteditable='true']",
        )
      ) {
        return;
      }
      // A live text selection: leave the native menu so it can be copied.
      if (window.getSelection()?.toString().trim()) return;
      // From here the right-click is ours.
      event.preventDefault();
      // Inside our own overlay / menu / indicator: don't stack another menu.
      if (target?.closest("[data-snip-ui]")) return;
      if (phase !== "idle") return;
      setMenuAt({ x: event.clientX, y: event.clientY });
      setPhase("menu");
    }
    document.addEventListener("contextmenu", onContext);
    return () => document.removeEventListener("contextmenu", onContext);
  }, [phase]);

  // Escape backs out of any step.
  useEffect(() => {
    if (phase === "idle" || phase === "review") return; // the modal owns Escape
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") reset();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [phase, reset]);

  // Object URL for the preview image, revoked when it changes or unmounts.
  useEffect(() => {
    if (!shot) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(shot);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [shot]);

  const capture = useCallback(async (rect: Rect) => {
    setPhase("capturing");
    // Let React drop the overlay before the paint html2canvas reads.
    await new Promise((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(resolve)),
    );
    try {
      const { default: html2canvas } = await import("html2canvas");
      const scale = Math.min(window.devicePixelRatio || 1, 2);
      const canvas = await html2canvas(document.body, {
        x: window.scrollX + rect.x,
        y: window.scrollY + rect.y,
        width: rect.w,
        height: rect.h,
        scale,
        useCORS: true,
        logging: false,
        backgroundColor:
          getComputedStyle(document.body).backgroundColor || "#ffffff",
        ignoreElements: (el) => el.hasAttribute?.("data-snip-ui"),
      });
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/png"),
      );
      setShot(blob);
    } catch (err) {
      // A failed capture still lets them file the words -- better than losing
      // the report because the picture did not render.
      console.error("feedback snip: capture failed", err);
      setShot(null);
    }
    setPhase("review");
  }, []);

  const onSent = useCallback(() => {
    setShot(null);
    reset();
    setToast("Feedback sent — thanks.");
    window.setTimeout(() => setToast(null), 3000);
  }, [reset]);

  return (
    <>
      {phase === "menu" && menuAt ? (
        <ContextMenu at={menuAt} onPick={() => setPhase("snip")} onClose={reset} />
      ) : null}
      {phase === "snip" ? <SnipOverlay onDone={capture} onCancel={reset} /> : null}
      {phase === "capturing"
        ? createPortal(
            <div className="snip-capturing" data-snip-ui>
              <span className="snip-spinner" aria-hidden="true" />
              Capturing…
            </div>,
            document.body,
          )
        : null}
      {phase === "review" ? (
        <ReviewModal
          preview={preview}
          shot={shot}
          page={location.pathname}
          onClose={reset}
          onSent={onSent}
        />
      ) : null}
      {toast
        ? createPortal(
            <div className="toast good snip-toast" data-snip-ui role="status">
              {toast}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

/** The one-button menu at the cursor. Closes on an outside click or a scroll. */
function ContextMenu({
  at,
  onPick,
  onClose,
}: {
  at: Point;
  onPick: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function onDown(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose();
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    // A later tick, so the click that opened the menu does not close it.
    const id = window.setTimeout(() => {
      document.addEventListener("mousedown", onDown);
    }, 0);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onClose, true);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onClose, true);
    };
  }, [onClose]);

  const left = Math.min(at.x, window.innerWidth - 200);
  const top = Math.min(at.y, window.innerHeight - 70);

  return createPortal(
    <div
      ref={ref}
      className="snip-menu"
      data-snip-ui
      role="menu"
      style={{ left, top }}
    >
      <button
        type="button"
        className="snip-menu-item"
        role="menuitem"
        autoFocus
        onClick={onPick}
      >
        <SnipGlyph />
        Send feedback
      </button>
    </div>,
    document.body,
  );
}

/** The dimmed full-screen overlay you drag a box on. */
function SnipOverlay({
  onDone,
  onCancel,
}: {
  onDone: (rect: Rect) => void;
  onCancel: () => void;
}) {
  const [start, setStart] = useState<Point | null>(null);
  const [cur, setCur] = useState<Point | null>(null);
  const rect = start && cur ? rectOf(start, cur) : null;

  return createPortal(
    <div
      className="snip-overlay"
      data-snip-ui
      onMouseDown={(e) => {
        if (e.button !== 0) return;
        const p = { x: e.clientX, y: e.clientY };
        setStart(p);
        setCur(p);
      }}
      onMouseMove={(e) => {
        if (start) setCur({ x: e.clientX, y: e.clientY });
      }}
      onMouseUp={() => {
        if (rect && rect.w > 6 && rect.h > 6) onDone(rect);
        else onCancel();
      }}
    >
      {rect ? (
        <div
          className="snip-rect"
          style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
        />
      ) : (
        <div className="snip-overlay-hint">Drag over the area to send · Esc to cancel</div>
      )}
    </div>,
    document.body,
  );
}

/** Review the snip, add a line, file it. Reuses the Feedback API verbatim. */
function ReviewModal({
  preview,
  shot,
  page,
  onClose,
  onSent,
}: {
  preview: string | null;
  shot: Blob | null;
  page: string;
  onClose: () => void;
  onSent: () => void;
}) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [kind, setKind] = useState<FeedbackKind>("bug");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  // Land the cursor in the title box the instant the snip is reviewed, so the
  // flow is: drag → type → Enter (Jonathan, 2026-10-22: "auto focus and enter
  // to send. make it easy to use"). `Modal` focuses its own panel on open, and
  // that runs after this child's effects, so a plain `autoFocus` loses the
  // race -- the rAF puts our focus call after the panel's.
  useEffect(() => {
    const id = requestAnimationFrame(() => titleRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const { data: item } = await api.fileFeedback({
        title: title.trim(),
        body: body.trim() || undefined,
        kind,
        page: page || undefined,
      });
      if (shot) {
        try {
          await api.attachScreenshot(item.id, shot);
        } catch (err) {
          setError(
            `Filed; the screenshot did not attach: ${err instanceof ApiError ? err.detail : "something went wrong"}.`,
          );
          setBusy(false);
          return;
        }
      }
      onSent();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Something went wrong.");
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Send feedback"
      onClose={onClose}
      actions={
        <>
          <button
            type="submit"
            form="snip-form"
            className="primary"
            disabled={busy || !title.trim()}
          >
            {busy ? "Sending…" : "Send it"}
          </button>
          <button type="button" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </>
      }
    >
      <form id="snip-form" onSubmit={submit} className="group-form" data-snip-ui>
        {error ? <div className="form-error">{error}</div> : null}
        {preview ? (
          <div className="snip-review-shot">
            <img src={preview} alt="The area you snipped" />
          </div>
        ) : (
          <div className="snip-review-noshot">
            No screenshot was captured — your note will still be sent.
          </div>
        )}
        <div className="field">
          <label htmlFor="snip-title">What's up?</label>
          <input
            ref={titleRef}
            id="snip-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={160}
            autoFocus
            required
            placeholder="One line — what's wrong or what to change, then Enter"
          />
        </div>
        <div className="field">
          <label htmlFor="snip-body">More (optional)</label>
          <textarea
            id="snip-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            maxLength={8000}
          />
        </div>
        <div className="field">
          <label htmlFor="snip-kind">Kind</label>
          <select
            id="snip-kind"
            value={kind}
            onChange={(e) => setKind(e.target.value as FeedbackKind)}
          >
            {(Object.keys(KIND_LABEL) as FeedbackKind[]).map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </select>
        </div>
      </form>
    </Modal>
  );
}

function SnipGlyph() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 7h3l1-2h12l1 2h3v13H2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}
